import React, { useState, useEffect } from 'react';

import { DeleteOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { Modal, Table, InputNumber, Button, Space, Popconfirm, message, Tag, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

interface ParcelItem {
  fulfillment_item_id?: string;
  product_id?: string;
  product_sku?: string;
  product_name?: string;
  batch?: string | null;
  quantity: number;
  serial_numbers?: string[];
  required_serial_numbers_count?: number;
  [key: string]: unknown;
}

interface Parcel {
  parcel_items?: ParcelItem[];
  [key: string]: unknown;
}

export interface UpdatedParcelItem {
  fulfillment_item_id?: string;
  product_id?: string;
  product_sku?: string;
  product_name?: string;
  quantity: number;
  batch?: string | null;
  serial_numbers: string[];
  required_serial_numbers_count: number;
}

interface ParcelItemsManagementModalProps {
  open: boolean;
  parcel: Parcel | null;
  parcelIndex: number;
  onConfirm: (updatedItems: UpdatedParcelItem[]) => void;
  onCancel: () => void;
  loading?: boolean;
}

interface ManagedItem extends ParcelItem {
  key: number;
  originalQuantity: number;
}

const ParcelItemsManagementModal: React.FC<ParcelItemsManagementModalProps> = ({
  open,
  parcel,
  parcelIndex,
  onConfirm,
  onCancel,
  loading = false
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<ManagedItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [newSerialInputs, setNewSerialInputs] = useState<Record<number, string>>({}); // { itemKey: "serial_value" }

  // 當 Modal 開啟時，初始化 items
  useEffect(() => {

    if (open && parcel && parcel.parcel_items) {

      const initializedItems: ManagedItem[] = parcel.parcel_items.map((item, index) => {

        return {
          ...item,
          key: index,
          originalQuantity: item.quantity
        };
      });

      setItems(initializedItems);
      setHasChanges(false);
    }
  }, [open]);

  const handleQuantityChange = (key: number, newQuantity: number | null) => {
    setItems(prev => prev.map(item => {
      if (item.key === key) {
        return { ...item, quantity: newQuantity as number };
      }
      return item;
    }));
    setHasChanges(true);
  };

  const handleRemoveItem = (key: number) => {
    setItems(prev => prev.filter(item => item.key !== key));
    setHasChanges(true);
  };

  const handleRemoveSerialNumber = (itemKey: number, serialIndex: number) => {

    setItems(prev => {

      const newItems = prev.map(item => {
        if (item.key === itemKey) {

          const newSerialNumbers = [...(item.serial_numbers || [])];
          const removedSerial = newSerialNumbers[serialIndex];

          newSerialNumbers.splice(serialIndex, 1);

          // 計算新的數量
          const requiredSerialNumbersCount = item.required_serial_numbers_count || 1;
          const newQuantity = Math.floor(newSerialNumbers.length / requiredSerialNumbersCount);


          const updatedItem: ManagedItem = {
            ...item,
            serial_numbers: newSerialNumbers,
            quantity: newQuantity
          };


          return updatedItem;
        }
        return item;
      });

      return newItems;
    });

    setHasChanges(true);
  };

  const handleAddSerialNumber = (itemKey: number) => {
    const serialValue = newSerialInputs[itemKey]?.trim();

    if (!serialValue) {
      message.warning(t('operator.qc.parcel_management.serial_number_required', { defaultValue: '請輸入序號' }));
      return;
    }

    const targetItem = items.find(item => item.key === itemKey);
    const isDuplicate = targetItem && targetItem.serial_numbers && targetItem.serial_numbers.includes(serialValue);

    if (isDuplicate) {
      message.error(t('operator.qc.parcel_management.serial_number_duplicate', { defaultValue: '序號已存在' }));
      return;
    }

    setItems(prev => prev.map(item => {
      if (item.key === itemKey) {
        const newSerialNumbers = [...(item.serial_numbers || []), serialValue];
        const requiredSerialNumbersCount = item.required_serial_numbers_count || 1;
        const newQuantity = Math.floor(newSerialNumbers.length / requiredSerialNumbersCount);


        return {
          ...item,
          serial_numbers: newSerialNumbers,
          quantity: newQuantity
        };
      }
      return item;
    }));

    // 清空輸入框
    setNewSerialInputs(prev => ({ ...prev, [itemKey]: '' }));
    setHasChanges(true);
  };

  const handleSerialInputChange = (itemKey: number, value: string) => {
    setNewSerialInputs(prev => ({ ...prev, [itemKey]: value }));
  };

  const handleSerialInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, itemKey: number) => {
    if (e.key === 'Enter') {
      handleAddSerialNumber(itemKey);
    }
  };

  const handleConfirm = () => {

    // 過濾規則：
    // - 對於需要序號的商品（required_serial_numbers_count > 0）：只有當序號被清空時才移除
    // - 對於不需要序號的商品：當數量為 0 時移除
    const validItems = items.filter(item => {
      if ((item.required_serial_numbers_count ?? 0) > 0) {
        // 需要序號的商品：只有序號清空時才移除
        const hasSerialNumbers = item.serial_numbers && item.serial_numbers.length > 0;
        return hasSerialNumbers;
      }
      // 不需要序號的商品：依數量判斷
      const keep = item.quantity > 0;
      return keep;

    });


    // 如果有商品的數量 < 0（這不應該發生，但加入防護）
    const hasInvalidQuantity = items.some(item => item.quantity < 0);
    if (hasInvalidQuantity) {
      message.error(t('operator.qc.parcel_management.quantity_must_be_positive'));
      return;
    }

    // 構建更新後的 parcel_items
    // fulfillment_item_id 是後端 PATCH /fulfillments/:id/parcels/:parcel_id 必填欄位
    // （parcels_controller.rb:43-47），用來對應 fulfillment_items 做容量檢查；缺則 422。
    const updatedItems: UpdatedParcelItem[] = validItems.map(item => ({
      fulfillment_item_id: item.fulfillment_item_id,
      product_id: item.product_id,
      product_sku: item.product_sku,
      product_name: item.product_name,
      quantity: item.quantity,
      batch: item.batch,
      serial_numbers: item.serial_numbers || [],
      required_serial_numbers_count: item.required_serial_numbers_count || 0
    }));


    // 如果會清空 parcel，顯示確認對話框
    if (updatedItems.length === 0) {
      let confirmInput = '';

      Modal.confirm({
        title: t('operator.qc.parcel_management.confirm_remove_parcel_title', {
          defaultValue: '確認移除箱子'
        }),
        content: (
          <div>
            <p style={{ marginBottom: '16px' }}>
              {t('operator.qc.parcel_management.confirm_remove_parcel_message', {
                number: parcelIndex + 1,
                defaultValue: `您即將移除箱子 ${parcelIndex + 1}。此操作無法復原。`
              })}
            </p>
            <p style={{ marginBottom: '8px', fontWeight: 500 }}>
              {t('operator.qc.parcel_management.type_yes_to_confirm', {
                defaultValue: '請輸入 "yes" 來確認：'
              })}
            </p>
            <Input
              placeholder="yes"
              onChange={(e) => {
                confirmInput = e.target.value;
              }}
              onPressEnter={(e) => {
                if ((e.target as HTMLInputElement).value.toLowerCase() === 'yes') {
                  Modal.destroyAll();
                  onConfirm(updatedItems);
                }
              }}
            />
          </div>
        ),
        okText: t('common.button.confirm') as string,
        cancelText: t('common.button.cancel') as string,
        okButtonProps: { danger: true },
        onOk: () => {
          if (confirmInput.toLowerCase() === 'yes') {
            onConfirm(updatedItems);
          } else {
            message.error(t('operator.qc.parcel_management.please_type_yes', {
              defaultValue: '請輸入 "yes" 來確認移除'
            }));
            return Promise.reject(new Error('Confirmation canceled'));
          }
        }
      });
    } else {
      // 不會清空 parcel，直接儲存
      onConfirm(updatedItems);
    }
  };

  const columns: ColumnsType<ManagedItem> = [
    {
      title: t('operator.qc.parcel_management.product_sku', { defaultValue: '商品 SKU' }),
      dataIndex: 'product_sku',
      key: 'product_sku',
      width: 150,
      render: (sku: string) => <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{sku}</span>
    },
    {
      title: t('operator.qc.parcel_management.product_name', { defaultValue: '商品名稱' }),
      dataIndex: 'product_name',
      key: 'product_name',
      ellipsis: true
    },
    {
      title: t('operator.qc.parcel_management.batch', { defaultValue: '批號' }),
      dataIndex: 'batch',
      key: 'batch',
      width: 120,
      render: (batch: string | null | undefined) => batch || '--'
    },
    {
      title: t('operator.qc.parcel_management.quantity', { defaultValue: '數量' }),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (quantity: number, record: ManagedItem) => {
        const hasSerialNumbers = record.serial_numbers && record.serial_numbers.length > 0;
        const isIncomplete = hasSerialNumbers && quantity === 0;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <InputNumber
              min={1}
              value={quantity}
              onChange={(value) => handleQuantityChange(record.key, value as number | null)}
              style={{
                width: '100%',
                ...(isIncomplete ? { borderColor: '#f59e0b' } : {})
              }}
              disabled={hasSerialNumbers}
            />
            {isIncomplete && (
              <span style={{
                color: '#f59e0b',
                fontSize: '12px',
                whiteSpace: 'nowrap'
              }}>
                序號不足
              </span>
            )}
          </div>
        );
      }
    },
    {
      title: t('operator.qc.parcel_management.serial_numbers', { defaultValue: '序號' }),
      dataIndex: 'serial_numbers',
      key: 'serial_numbers',
      render: (serialNumbers: string[] | undefined, record: ManagedItem) => {
        const requiredCount = record.required_serial_numbers_count || 0;
        const needSerialNumber = requiredCount > 0;

        // 不需要序號的商品直接顯示 --
        if (!needSerialNumber) {
          return <span style={{ color: '#94a3b8' }}>--</span>;
        }

        const hasSerialNumbers = serialNumbers && serialNumbers.length > 0;
        const currentCount = serialNumbers ? serialNumbers.length : 0;
        const isIncomplete = currentCount % requiredCount !== 0;

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* 顯示現有序號 */}
            {hasSerialNumbers && (
              <Space size={4} wrap>
                {(() => {

                  return serialNumbers!.map((serial, index) => {
                    // 檢查是否重複（在當前序號之前是否已存在相同序號）
                    const isDuplicate = serialNumbers!.slice(0, index).includes(serial);

                    // 使用組合 key 確保唯一性（序號值 + index）
                    const uniqueKey = `${serial}-${index}`;


                    return (
                      <Tag
                        key={uniqueKey}
                        closable
                        onClose={() => handleRemoveSerialNumber(record.key, index)}
                        color={isDuplicate ? 'red' : undefined}
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          marginBottom: '4px'
                        }}
                      >
                        {serial}
                      </Tag>
                    );
                  });
                })()}
              </Space>
            )}

            {/* 序號不足時顯示輸入框 */}
            {isIncomplete && (
              <Input
                placeholder={t('operator.qc.parcel_management.add_serial_number', { defaultValue: '輸入序號後按 Enter...' }) as string}
                value={newSerialInputs[record.key] || ''}
                onChange={(e) => handleSerialInputChange(record.key, e.target.value)}
                onKeyPress={(e) => handleSerialInputKeyPress(e, record.key)}
                style={{
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }}
                size="small"
              />
            )}
          </div>
        );
      }
    },
    {
      title: t('operator.qc.parcel_management.actions', { defaultValue: '操作' }),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record: ManagedItem) => (
        <Popconfirm
          title={t('operator.qc.parcel_management.confirm_remove_item', { defaultValue: '確定要移除這個品項嗎？' })}
          onConfirm={() => handleRemoveItem(record.key)}
          okText={t('common.button.confirm') as string}
          cancelText={t('common.button.cancel') as string}
        >
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
      )
    }
  ];

  const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const totalItems = items.length;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>{t('operator.qc.parcel_management.title', { number: parcelIndex + 1, defaultValue: `管理箱子 ${parcelIndex + 1}` })}</span>
          <Tag color="blue">
            {t('operator.qc.parcel_management.summary', { items: totalItems, quantity: totalQuantity, defaultValue: `${totalItems} 項 / ${totalQuantity} 件` })}
          </Tag>
        </div>
      }
      open={open}
      onCancel={onCancel}
      width={1000}
      confirmLoading={loading}
      footer={
        <Space>
          <Button icon={<CloseOutlined />} onClick={onCancel} disabled={loading}>
            {t('common.button.cancel')}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleConfirm}
            disabled={!hasChanges}
            loading={loading}
          >
            {t('common.save', { defaultValue: '儲存' })}
          </Button>
        </Space>
      }
      destroyOnHidden
    >
      <div style={{ marginBottom: '16px' }}>
        <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '8px' }}>
          {t('operator.qc.parcel_management.description', {
            defaultValue: '您可以調整商品數量、移除序號或刪除整個品項。如果商品有序號，數量會自動根據序號數量計算。'
          })}
        </div>
        {items.some(item => (item.required_serial_numbers_count ?? 0) > 0 && item.quantity === 0) && (
          <div style={{
            padding: '8px 12px',
            background: '#fef3c7',
            borderRadius: '6px',
            color: '#92400e',
            fontSize: '12px'
          }}>
            ⚠️ 序號不足的商品（數量為 0）將在儲存時自動移除
          </div>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={items}
        pagination={false}
        size="small"
        scroll={{ x: 800 }}
        locale={{
          emptyText: t('operator.qc.parcel_management.no_items', { defaultValue: '尚未加入商品' })
        }}
      />

      {hasChanges && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            background: '#fef3c7',
            borderRadius: '8px',
            color: '#92400e',
            fontSize: '13px'
          }}
        >
          {t('operator.qc.parcel_management.unsaved_changes', {
            defaultValue: '⚠️ 您有未儲存的變更，請記得點擊「儲存」按鈕'
          })}
        </div>
      )}
    </Modal>
  );
};

export default ParcelItemsManagementModal;
